import { SEARCH_FIELDS, Resource } from './resource';

export type SearchHit = { field: string; snippet: string; score: number };
export type SearchResult = Resource & { score: number; hits: SearchHit[] };

const WEIGHTS: Record<string, number> = {
  资源名称: 8, 一句话价值: 5, 标签: 4, 主赛道: 4, 资源类型: 4,
  适用对象: 3, 前置条件: 3, 保存方式: 2, 资源正文: 2
};

export function normalizeQuery(value: string): string {
  return value.normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
}

function tokensOf(query: string): string[] {
  return normalizeQuery(query).split(' ').filter(Boolean);
}

function makeSnippet(text: string, tokens: string[]): string {
  const normalized = text.normalize('NFKC');
  const lower = normalized.toLocaleLowerCase();
  const position = tokens.map((token) => lower.indexOf(token)).filter((index) => index >= 0).sort((a, b) => a - b)[0] ?? 0;
  const start = Math.max(0, position - 60);
  const end = Math.min(normalized.length, start + 160);
  return `${start > 0 ? '…' : ''}${normalized.slice(start, end)}${end < normalized.length ? '…' : ''}`;
}

export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const tokens = tokensOf(query).sort((a, b) => b.length - a.length);
  if (!tokens.length || !text) return [{ text, match: false }];
  const regex = new RegExp(`(${tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'giu');
  return text.split(regex).filter(Boolean).map((part) => ({ text: part, match: tokens.some((token) => normalizeQuery(part) === token) }));
}

export function searchResources(resources: Resource[], query: string): SearchResult[] {
  const normalizedQuery = normalizeQuery(query);
  const tokens = tokensOf(normalizedQuery);
  if (!tokens.length) return [];

  return resources.map((resource) => {
    const hits: SearchHit[] = [];
    let score = 0;
    for (const field of SEARCH_FIELDS) {
      const text = resource.fields[field] ?? '';
      const normalized = normalizeQuery(text);
      if (!normalized) continue;
      const matched = tokens.filter((token) => normalized.includes(token));
      if (matched.length) {
        score += WEIGHTS[field] * matched.length;
        if (normalized.includes(normalizedQuery) && tokens.length > 1) score += 10;
        hits.push({ field, snippet: makeSnippet(text, matched), score: WEIGHTS[field] * matched.length });
      }
    }
    return { ...resource, score, hits };
  }).filter((result) => result.hits.length > 0 && tokens.every((token) => SEARCH_FIELDS.some((field) => normalizeQuery(result.fields[field] ?? '').includes(token))))
    .sort((a, b) => b.score - a.score || (a.fields['资源名称'] ?? '').localeCompare(b.fields['资源名称'] ?? '', 'zh-CN'));
}
